import Data from '@/data'
import { ItemFlags } from '@/data/enums'
import { baseMaxQuality, itemAlwaysCorrupted, itemBaseIcon, itemCreate, itemFixImplicit } from '@/logic/item'
import { WithItemTemplateTooltip } from '@/tooltip'
import { Item, ItemRarity } from '@/types/item'
import Select, { OptionOrGroup, SelectOption, underlinerFunc } from '@/ui/select'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Checkbox } from '@maxroll-media-group/common-libs'
import React, { ReactNode } from 'react'
import { SmallIcon } from '../Icon'
import HoverNumber from '../editor/HoverNumber'

type ItemProps = {
  item: Item
  onChange: (value: Item | ((prev: Item) => Item)) => void
}

type ItemOption = {
  id: string
  name: string
  color?: string
}

const renderBase = ({ id, name, color }: ItemOption, search?: RegExp) => (
  <WithItemTemplateTooltip name={id}>
    <span className={color ? `poe-item-${color}` : undefined}>{underlinerFunc(name, search)}</span>
  </WithItemTemplateTooltip>
)
const renderBaseIcon = ({ id, name, color }: ItemOption, search?: RegExp) => (
  <WithItemTemplateTooltip name={id}>
    <span className={color ? `poe-item-${color}` : undefined}>
      <SmallIcon icon={itemBaseIcon(Data.items[id])} /> {underlinerFunc(name, search)}
    </span>
  </WithItemTemplateTooltip>
)

function EditItemBase({ item, onChange }: ItemProps) {
  const base = Data.items[item.base]
  const onSetBase = React.useCallback(
    (opt?: ItemOption) => opt && onChange((item) => itemCreate(opt.id, item)!),
    [onChange]
  )
  const getOptions = React.useCallback((): OptionOrGroup<ItemOption>[] => {
    const groups: Record<string, ItemOption[]> = {}
    for (const item of Data.itemByType[base.item_class]) {
      let group = 'Other'
      if (item.properties) {
        const def = []
        if (item.properties.armour) def.push('Armour')
        if (item.properties.evasion) def.push('Evasion')
        if (item.properties.energy_shield) def.push('Energy Shield')
        if (def.length) group = def.join(' / ')
      }
      groups[group] = groups[group] || []
      groups[group].push({ id: item.id, name: item.name })
    }
    const names = Object.keys(groups)
    if (names.length === 1) return groups[names[0]]
    return names.map((name) => ({ title: name, options: groups[name] }))
  }, [base.item_class])

  return (
    <Select
      options={getOptions}
      value={item.base ? { id: item.base, name: Data.items[item.base]?.name } : undefined}
      onChange={onSetBase}
      searchLimit={10}
      searchMiddle
      renderValue={renderBase}
      renderOption={renderBaseIcon}
      className="ui-Select poe-item-base"
    />
  )
}

export function EditAbyssBase({ item, onChange }: ItemProps) {
  const base = Data.items[item.base]
  const onSetBase = React.useCallback(
    (opt?: ItemOption) => opt && onChange((item) => itemCreate(opt.id, item)!),
    [onChange]
  )
  const getOptions = React.useCallback((): OptionOrGroup<ItemOption>[] => {
    const normal: ItemOption[] = []
    for (const item of Data.itemByType[base.item_class]) {
      normal.push({ id: item.id, name: item.name })
    }
    const uniques: ItemOption[] = []
    for (const [id, unique] of Object.entries(Data.uniques)) {
      const ubase = Data.items[unique.variants[0].base]
      if (!ubase || ubase.item_class !== base.item_class) continue
      uniques.push({ id, name: unique.name, color: 'unique' })
    }
    return [
      {
        title: 'Normal',
        options: normal
      },
      {
        title: 'Unique',
        options: uniques
      }
    ]
  }, [base.item_class])

  return (
    <Select
      options={getOptions}
      value={
        item.base
          ? {
              id: item.base,
              name: (item.unique ? Data.uniques[item.unique] : Data.items[item.base])?.name ?? 'Unknown',
              color: item.unique ? 'unique' : undefined
            }
          : undefined
      }
      onChange={onSetBase}
      searchLimit={10}
      searchMiddle
      renderValue={renderBase}
      renderOption={renderBaseIcon}
      className="ui-Select poe-item-base"
    />
  )
}

type RarityOption = SelectOption<ItemRarity>

function EditItemRarity({ item, onChange }: ItemProps) {
  const base = Data.items[item.base]
  const template = Data.item_templates[base.inherits]
  const options = React.useMemo<RarityOption[]>(
    () =>
      [
        { id: 'normal', name: 'Normal' } as const,
        { id: 'magic', name: 'Magic' } as const,
        { id: 'rare', name: 'Rare' } as const
      ].filter((r) => !template.disable_rarity?.includes(r.id)),
    [template]
  )
  const onSetRarity = React.useCallback(
    (opt?: RarityOption) => opt && onChange((item) => ({ ...item, rarity: opt.id })),
    [onChange]
  )
  return (
    <Select
      options={options}
      value={options.find((opt) => opt.id === item.rarity)}
      onChange={onSetRarity}
      className="ui-Select"
    />
  )
}

type VariantOption = {
  id: number
  name: string
}

function EditItemVariant({ item, onChange }: ItemProps) {
  const unique = Data.uniques[item.unique!]
  const options = React.useMemo(() => unique.variants.map(({ name }, id) => ({ id, name })), [unique])
  const onSetVariant = React.useCallback(
    (opt?: VariantOption) => opt && onChange((item) => itemCreate(item.unique!, item, opt.id)!),
    [onChange]
  )
  const renderOption = React.useCallback(
    ({ id, name }: VariantOption) => (
      <WithItemTemplateTooltip name={item.unique!} variant={id}>
        <span>{name}</span>
      </WithItemTemplateTooltip>
    ),
    [item.unique]
  )
  return (
    <Select
      options={options}
      value={options.find((opt) => opt.id === item.variant)}
      onChange={onSetVariant}
      renderOption={renderOption}
      className="ui-Select"
    />
  )
}

function EditMapTier({ item, onChange }: ItemProps) {
  const onSetTier = React.useCallback((value: number) => onChange((item) => ({ ...item, map_tier: value })), [onChange])
  return <HoverNumber min={1} max={17} step={1} value={item.map_tier ?? 1} onChange={onSetTier} />
}

function EditItemLevel({ item, onChange }: ItemProps) {
  const onSetLevel = React.useCallback((value: number) => onChange((item) => ({ ...item, ilvl: value })), [onChange])
  return <HoverNumber min={1} max={100} step={1} value={item.ilvl} onChange={onSetLevel} />
}

function EditItemQuality({ item, onChange }: ItemProps) {
  const onSetQuality = React.useCallback(
    (value: number) => onChange((item) => ({ ...item, quality: value })),
    [onChange]
  )
  return <HoverNumber min={0} max={30} step={1} value={item.quality} onChange={onSetQuality} />
}

function EditItemCorrupted({ item, onChange }: ItemProps) {
  const onSetCorrupted = React.useCallback(
    (checked: boolean) => {
      const value = checked ? ItemFlags.Corrupted : 0
      onChange((item) =>
        itemFixImplicit({
          ...item,
          flags: (item.flags & ~ItemFlags.Corrupted) | value
        })
      )
    },
    [onChange]
  )
  return (
    <Checkbox
      value={!!(item.flags & ItemFlags.Corrupted)}
      onChange={onSetCorrupted}
      disabled={itemAlwaysCorrupted(item)}
    />
  )
}

type InfluenceOption = SelectOption<ItemFlags>

export function ItemInfluenceEditor({
  value,
  onChange
}: {
  value: number
  onChange: (value: number | ((prev: number) => number)) => void
}) {
  const options = React.useMemo<InfluenceOption[]>(
    () => [
      { id: ItemFlags.Shaper, name: 'Shaper' },
      { id: ItemFlags.Elder, name: 'Elder' },
      { id: ItemFlags.Crusader, name: 'Crusader' },
      { id: ItemFlags.Hunter, name: 'Hunter' },
      { id: ItemFlags.Redeemer, name: 'Redeemer' },
      { id: ItemFlags.Warlord, name: 'Warlord' },
      { id: ItemFlags.SearingExarch, name: 'Searing Exarch' },
      { id: ItemFlags.EaterOfWorlds, name: 'Eater of Worlds' },
      { id: ItemFlags.Fractured, name: 'Fractured' },
      { id: ItemFlags.Synthesized, name: 'Synthesized' },
      { id: ItemFlags.Veiled, name: 'Veiled' },
      { id: ItemFlags.Archnemesis, name: 'Archnemesis' },
      { id: ItemFlags.Experimented, name: 'Experimented' }
    ],
    []
  )
  const renderValue = React.useCallback(
    () =>
      options
        .filter((opt) => value & opt.id)
        .map((opt) => {
          function onClick(e: React.UIEvent) {
            e.stopPropagation()
            onChange((value) => value & ~opt.id)
          }
          return (
            <span key={opt.id} className={`poe-value poe-influence-${Math.round(Math.log2(opt.id))}`}>
              {opt.name} <FontAwesomeIcon className="poe-remove" icon={faTimes} onClick={onClick} />
            </span>
          )
        }),
    [options, value, onChange]
  )
  const renderOption = React.useCallback(
    (opt: InfluenceOption) => (
      <span className={`poe-option poe-influence-${Math.round(Math.log2(opt.id))}`}>{opt.name}</span>
    ),
    []
  )
  const onSelect = React.useCallback((opt?: InfluenceOption) => opt && onChange((value) => value | opt.id), [onChange])
  return (
    <Select
      className="ui-Select poe-ItemInfluence"
      valueClass="ui-Select-value"
      options={options}
      value={options.find((opt) => value & opt.id)}
      onChange={onSelect}
      renderValue={renderValue}
      renderOption={renderOption}
      disabled={options.filter((opt) => value & opt.id).length >= 2}
      placeholder={<span className="poe-none">None</span>}
    />
  )
}

function EditItemInfluence({ item, onChange }: ItemProps) {
  const onChangeInfluence = React.useCallback(
    (value: number | ((prev: number) => number)) =>
      onChange((item) => {
        const prev = item.flags
        if (typeof value === 'function') value = value(prev)
        if (value === prev) return item
        return { ...item, flags: value }
      }),
    [onChange]
  )
  return <ItemInfluenceEditor value={item.flags} onChange={onChangeInfluence} />
}

export function ItemOptions({
  item,
  onChange,
  children,
  noBase
}: ItemProps & { children?: ReactNode; noBase?: boolean }) {
  const onRename = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange((item) => ({ ...item, name: e.target.value })),
    [onChange]
  )
  const base = Data.items[item.base]
  return (
    <div className="poe-item-options">
      {children}
      <label>
        <span>Name</span>
        <input type="text" value={item.name || ''} onChange={onRename} placeholder="Empty" />
      </label>
      {!item.unique && !noBase && (
        <label>
          <span>Base</span>
          <EditItemBase item={item} onChange={onChange} />
        </label>
      )}
      {!item.unique && (
        <label className="poe-small">
          <span>Rarity</span>
          <EditItemRarity item={item} onChange={onChange} />
        </label>
      )}
      {!!item.unique && (
        <label>
          <span>Variant</span>
          <EditItemVariant item={item} onChange={onChange} />
        </label>
      )}
      {base.domain === 'area' ? (
        <label className="poe-small">
          <span>Map Tier</span>
          <EditMapTier item={item} onChange={onChange} />
        </label>
      ) : (
        <label className="poe-small">
          <span>Item Level</span>
          <EditItemLevel item={item} onChange={onChange} />
        </label>
      )}
      {!!baseMaxQuality(item.base) && (
        <label className="poe-small">
          <span>Quality</span>
          <EditItemQuality item={item} onChange={onChange} />%
        </label>
      )}
      {!Data.items[item.base]?.tags.includes('flask') && (
        <label className="poe-small">
          <span>Corrupted</span>
          <EditItemCorrupted item={item} onChange={onChange} />
        </label>
      )}
      <label className="poe-small">
        <span>Influence</span>
        <EditItemInfluence item={item} onChange={onChange} />
      </label>
    </div>
  )
}
